// Host-side recording orchestration (F-REC-1/2). The engine owns capture,
// crypto, and the trust indicator; this file owns the *layout* — how the live
// video sources are composited onto the recorder canvas — because layout is UI
// knowledge (SPEC §10, §9.3). It also drives the local-save + optional
// encrypted-upload flow after stop.

import { RecordingController, type RecordingResult } from '@brizina/call-engine';

import { api } from './api';
import { engine } from './engine';
import { callStore, pushToast, setRecordingLocally } from './store/callStore';

const controller = new RecordingController(engine);

// Cached <video> elements keyed by MediaStreamTrack id, so the draw loop reuses
// elements instead of recreating them every frame.
const videoEls = new Map<string, HTMLVideoElement>();

function videoFor(track: MediaStreamTrack): HTMLVideoElement {
  let el = videoEls.get(track.id);
  if (!el) {
    el = document.createElement('video');
    el.muted = true;
    el.playsInline = true;
    el.srcObject = new MediaStream([track]);
    void el.play().catch(() => {});
    videoEls.set(track.id, el);
  }
  return el;
}

/** Composite the current video sources into a grid, "contain"-fit per cell,
 * over a dark backdrop. Called ~30fps by the recorder. */
function drawFrame(ctx: CanvasRenderingContext2D, width: number, height: number): void {
  ctx.fillStyle = '#111318';
  ctx.fillRect(0, 0, width, height);

  const sources = engine.recordingVideoSources();
  // Prune video elements for tracks that are gone.
  const live = new Set(sources.map((s) => s.track.id));
  for (const [id, el] of videoEls) {
    if (!live.has(id)) {
      el.srcObject = null;
      videoEls.delete(id);
    }
  }
  if (sources.length === 0) {
    ctx.fillStyle = '#8b8f98';
    ctx.font = '28px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Waiting for video…', width / 2, height / 2);
    return;
  }

  const cols = Math.ceil(Math.sqrt(sources.length));
  const rows = Math.ceil(sources.length / cols);
  const cellW = width / cols;
  const cellH = height / rows;
  const gap = 6;

  sources.forEach((s, i) => {
    const el = videoFor(s.track);
    const cx = (i % cols) * cellW;
    const cy = Math.floor(i / cols) * cellH;
    const w = cellW - gap * 2;
    const h = cellH - gap * 2;
    const x = cx + gap;
    const y = cy + gap;

    ctx.fillStyle = '#000';
    ctx.fillRect(x, y, w, h);

    const vw = el.videoWidth || 16;
    const vh = el.videoHeight || 9;
    if (el.readyState >= 2 && vw > 0 && vh > 0) {
      const scale = Math.min(w / vw, h / vh);
      const dw = vw * scale;
      const dh = vh * scale;
      ctx.drawImage(el, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
    }

    // Name strip for legibility over video (§16 invariant).
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    ctx.fillRect(x, y + h - 26, w, 26);
    ctx.fillStyle = '#fff';
    ctx.font = '15px system-ui, sans-serif';
    ctx.textAlign = 'left';
    const label = s.source === 'screen' ? `${s.name} (screen)` : s.name;
    ctx.fillText(label, x + 8, y + h - 8);
  });
}

export function recordingSupported(): boolean {
  return RecordingController.supported();
}

export async function startRecording(): Promise<void> {
  if (controller.active) return;
  const me = engine.snapshots().find((a) => a.isLocal);
  const byName = me?.name || 'Host';
  try {
    await controller.start(byName, drawFrame);
    setRecordingLocally(true);
  } catch (err) {
    setRecordingLocally(false);
    pushToast(err instanceof Error ? err.message : 'Could not start recording', 'error');
  }
}

export async function stopRecording(): Promise<void> {
  if (!controller.active) return;
  let result: RecordingResult;
  try {
    result = await controller.stop();
  } catch (err) {
    setRecordingLocally(false);
    pushToast(err instanceof Error ? err.message : 'Recording failed to finalize', 'error');
    return;
  }
  setRecordingLocally(false);

  // §10.4(a): always save the plain webm locally for the recorder.
  const stamp = new Date().toISOString().replace(/[:.]/g, '-');
  saveBlob(result.blob, `brizina-recording-${stamp}.webm`);
  pushToast(`Recording saved (${formatSize(result.blob.size)})`, 'info');

  // §10.4(c): optionally upload an encrypted copy, if the meeting is E2EE and
  // it fits the presign size cap. The key stays with the recorder.
  const token = callStore.getState().roomToken;
  if (token && result.blob.size <= 100 << 20) {
    try {
      const up = await controller.uploadEncrypted(result, {
        putCiphertext: async (fileId, ciphertext) => {
          const { uploadUrl, objectKey } = await api.presignUpload(token, fileId, ciphertext.byteLength);
          const put = await fetch(uploadUrl, {
            method: 'PUT',
            body: ciphertext,
            headers: { 'Content-Type': 'application/octet-stream' },
          });
          if (!put.ok) throw new Error(`upload failed (${put.status})`);
          return objectKey;
        },
      });
      pushToast('Encrypted copy uploaded (key kept only by you)', 'info');
      // Keep the decryption material next to the object so the host can recover
      // it later; there is no server-side copy of the key.
      saveRecordingKeyNote(up.objectKey, up.nonceB64, up.fileId);
    } catch (err) {
      pushToast(err instanceof Error ? err.message : 'Encrypted upload failed', 'warning');
    }
  }
}

/** The recording is keyed off the room secret + fileId; write a small note the
 * host keeps so a later download can be decrypted. */
function saveRecordingKeyNote(objectKey: string, nonceB64: string, fileId: string): void {
  const note = [
    'Brizina encrypted recording — keep this to decrypt your copy.',
    `objectKey: ${objectKey}`,
    `fileId: ${fileId}`,
    `nonceB64: ${nonceB64}`,
  ].join('\n');
  saveBlob(new Blob([note], { type: 'text/plain' }), `${fileId}.brizina-key.txt`);
}

function saveBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

function formatSize(bytes: number): string {
  if (bytes >= 1 << 20) return `${(bytes / (1 << 20)).toFixed(1)} MB`;
  if (bytes >= 1 << 10) return `${Math.round(bytes / (1 << 10))} KB`;
  return `${bytes} B`;
}
